import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCheckoutApi } from '../checkout/useCheckoutApi'
import { clearAvailabilityCache } from './useSessionPicker'
import { useRequirePaymentProfile } from '../payment/useRequirePaymentProfile'
import { apiMessageKey } from '../useApiRequest'

import { useConfirm } from '../../contexts/ConfirmContext'
import {
  CheckoutRequest,
  CheckoutStatus,
  CheckoutType,
  Money,
  SelectedSession,
  SummaryLine,
} from '../../types/booking/booking'
import { ConfirmAsideVariant, ConfirmDetail, ConfirmLayout, CONFIRM_SKIP_NAVIGATION } from '../../types/confirm/confirm'
import { BookingRecipient, GuestBookingRecipient } from '../../types/court/court'
import { PaymentMethod } from '../../types/payment/payment'
import {
  attemptRequestId,
  attemptSignature,
  forgetAttempt,
  checkoutPath,
  formatMinutes,
  formatMoney,
  formatPoints,
  sessionKey,
  timeOf,
  toBookingItems,
} from '../../utils/booking/booking'
import { toDisplayOnlyDate } from '../../utils/dateUtils'

const ENDED_WITHOUT_BOOKING = [
  CheckoutStatus.EXPIRED,
  CheckoutStatus.PAYMENT_FAILED,
  CheckoutStatus.CANCELED,
  CheckoutStatus.REFUND_PENDING,
  CheckoutStatus.REFUNDED,
]

export enum CheckoutMode {
  CUSTOMER = 'CUSTOMER',
  MANAGER = 'MANAGER',
  GUEST = 'GUEST',
}

type StartCheckoutOptions = {
  mode: CheckoutMode
  sessions: SelectedSession[]
  lines: SummaryLine[]
  /** Price of the paid sessions and top-up (before refund points). */
  total?: Money
  /** Refund points the quote spends; the rest is paid by card or offline. */
  pointsAmount?: number
  /** Pay with refund points (default true; management can switch it off, guests have none). */
  useRefundPoints?: boolean
  topUpPassProductId?: number
  topUpName?: string
  recipient?: BookingRecipient | GuestBookingRecipient
  paymentMethod?: PaymentMethod
  /** Where "back" on the confirmation page returns, with the state needed to restore the selection. */
  returnTo: string
  returnState?: unknown
  /** Extra confirmation cards (e.g. guest contact details). */
  extraCards?: { id: string; title: string; details: ConfirmDetail[] }[]
  /** Called when the server reports the interval was taken meanwhile. */
  onConflict?: () => void
}

/**
 * Confirmation and checkout start. A checkout attempt keeps one clientRequestId (stored per selection), so confirming
 * twice, retrying after an error or confirming the same selection in another tab returns the same hold. The customer
 * then lands on the checkout page, which shows the hold, its server deadline and the payment button.
 */
export const useStartCheckout = () => {
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const { create, createGuest } = useCheckoutApi()
  const { requirePaymentProfile } = useRequirePaymentProfile()
  const { t, i18n } = useTranslation(['bookings', 'exceptions'])

  const startCheckout = (options: StartCheckoutOptions) => {
    const {
      mode,
      sessions,
      lines,
      total,
      pointsAmount = 0,
      useRefundPoints = true,
      topUpPassProductId,
      topUpName,
      recipient,
      paymentMethod,
      returnTo,
      returnState,
      extraCards = [],
      onConflict,
    } = options
    if (sessions.length === 0) {
      return
    }

    const amountDue = Math.round(((total?.amount ?? 0) - pointsAmount) * 100) / 100
    const due: Money | undefined = total ? { ...total, amount: amountDue } : undefined
    const payingByCard = (paymentMethod ?? PaymentMethod.CARD) === PaymentMethod.CARD
    if (
      mode === CheckoutMode.CUSTOMER &&
      payingByCard &&
      amountDue > 0 &&
      !requirePaymentProfile({
        returnTo,
        returnState: { ...(returnState as object), resumeConfirmation: true },
        cancelState: returnState,
      })
    ) {
      return
    }

    const signature = attemptSignature([
      mode,
      sessions.map(sessionKey),
      topUpPassProductId ?? null,
      recipient ?? null,
      paymentMethod ?? null,
      useRefundPoints,
    ])

    const request: CheckoutRequest = {
      clientRequestId: attemptRequestId(signature),
      type: CheckoutType.BOOKING,
      items: toBookingItems(sessions),
      // Eligible prepaid minutes are always used first; guests have none.
      usePasses: mode !== CheckoutMode.GUEST,
      topUpPassProductId: mode === CheckoutMode.GUEST ? null : (topUpPassProductId ?? null),
      recipient: recipient as BookingRecipient | undefined,
      paymentMethod: paymentMethod ?? PaymentMethod.CARD,
      useRefundPoints: mode !== CheckoutMode.GUEST && useRefundPoints,
    }

    const prepaidCount = lines.filter(line => line.prepaidMinutes >= line.durationMinutes).length
    const willPayOnline = payingByCard && amountDue > 0

    confirm(
      {
        title: t('bookings:booking-confirmation.title'),
        subtitle: t('booking.booking-courts.summary-panel.selected-slots', { itemsLength: lines.length }),
        itemCards: [
          ...lines.map(line => {
            const prepaid = line.prepaidMinutes >= line.durationMinutes
            return {
              id: line.key,
              title: line.courtName,
              subtitle: t('booking.booking-courts.summary-panel.slot-details', {
                date: toDisplayOnlyDate(line.startTime, i18n.language),
                time: timeOf(line.startTime),
                durMin: formatMinutes(line.durationMinutes),
              }),
              aside: prepaid ? t('booking.booking-courts.summary-panel.plan') : formatMoney(line.amount),
              asideVariant: prepaid ? ConfirmAsideVariant.INCLUDED : ConfirmAsideVariant.PAID,
            }
          }),
          ...extraCards.map(card => ({ ...card, detailsLayout: ConfirmLayout.DETAILS })),
        ],
        summary: {
          details: [
            ...(mode !== CheckoutMode.GUEST
              ? [
                  {
                    label: t('booking.booking-courts.summary-panel.plan-covered-slots'),
                    value: String(prepaidCount),
                  },
                ]
              : []),
            {
              label: t('booking.booking-courts.summary-panel.pay-per-play'),
              value: String(lines.length - prepaidCount),
            },
            ...(topUpName ? [{ label: t('booking.funding.top-up-label'), value: topUpName }] : []),
            ...(pointsAmount > 0
              ? [
                  {
                    label: t('booking.booking-courts.summary-panel.total-price'),
                    value: formatMoney(total),
                  },
                  {
                    label: t('booking.booking-courts.summary-panel.refund-points'),
                    value: t('booking.booking-courts.summary-panel.refund-points-value', {
                      points: formatPoints(pointsAmount),
                    }),
                  },
                ]
              : []),
            ...(mode === CheckoutMode.MANAGER && paymentMethod
              ? [
                  {
                    label: t('booking.booking-courts.summary-panel.payment-method-label'),
                    value: t(`booking.all-booking-list.payment-method.${paymentMethod.toLowerCase()}`),
                  },
                ]
              : []),
          ],
          total: {
            label: t(
              pointsAmount > 0
                ? 'booking.booking-courts.summary-panel.amount-due'
                : 'booking.booking-courts.summary-panel.total'
            ),
            value: formatMoney(due) || '—',
          },
        },
        warning: willPayOnline ? t('booking.checkout.hold-notice') : undefined,
        confirmLabel: willPayOnline
          ? t('booking.checkout.reserve-and-pay', { total: formatMoney(due) })
          : t('bookings:booking-confirmation.confirm-label'),
        backLabel: t('bookings:booking-confirmation.back-label'),
        showTermsLink: willPayOnline,
        allowIncompleteProfile: mode !== CheckoutMode.CUSTOMER,
        returnTo,
        returnState,
        onConfirm: async () => {
          try {
            const submit = (body: CheckoutRequest) =>
              mode === CheckoutMode.GUEST ? createGuest(body) : create(body)
            let checkout = await submit(request)
            if (checkout && ENDED_WITHOUT_BOOKING.includes(checkout.status)) {
              // The stored attempt belongs to an earlier hold that already ended: this is a new attempt.
              forgetAttempt(signature)
              checkout = await submit({ ...request, clientRequestId: attemptRequestId(signature) })
            }
            if (!checkout) {
              throw new Error(t('exceptions:unknown'))
            }
            clearAvailabilityCache()
            navigate(checkoutPath(checkout.holdId), { replace: true })
            return CONFIRM_SKIP_NAVIGATION
          } catch (caught) {
            if (apiMessageKey(caught) === 'availability-conflict') {
              onConflict?.()
            }
            throw caught
          }
        },
      },
      mode === CheckoutMode.GUEST ? '/guest-booking/confirm' : '/confirm'
    )
  }

  return { startCheckout }
}
